import api from 'services/api';
import forcaseApi from 'services/predictive-analytics-api';
import ReactGA from 'react-ga4';

/* The SpaceProps type represents the properties of a Space, including its UUID, name, description, buildingUuid, squareFeet, capacity, functionUuid, typeUuid, and unitUuid. */
export type SpaceProps = {
  uuid?: string;
  name: string;
  description: string;
  buildingUuid: string;
  squareFeet: number | string;
  capacity: number | string;
  functionUuid: string;
  typeUuid: string;
  unitUuid: string;
  smsAlertEnabled?: boolean;
  emailAlertEnabled?: boolean;
};

/* The SpaceAlertSettingsProps type represents the properties of space alerts */
export type SpaceAlertSettingsProps = {
  spaceUuid: string;
  smsAlertEnabled: boolean;
  emailAlertEnabled: boolean;
};

/* The SpaceAlertProps type represents the properties required to update spaces alerts and building emergency contact info */
export type SpaceAlertProps = {
  emergencyPhoneNumbers: string[];
  emergencyEmails: string[];
  Spaces: SpaceAlertSettingsProps[];
};

export type Aggregation = 'hourly' | 'daily' | 'monthly' | 'yearly';

export type AggregatedUniqueOccupantsProps = {
  buildingUuid: string;
  startDate: string; // use time.formatDateToString ("YYYY-MM-DD" format)
  endDate: string; // use time.formatDateToString ("YYYY-MM-DD" format)
  aggregation: Aggregation;
};

export type AggregatedUsageResponse = Array<{
  [uuid: string]: {
    x: string[];
    y: number[];
  };
}>;

export type BuildingAggregatedUsageResponse = {
  x: string[];
  y: number[];
};

export type SpacePopularityProps = {
  buildingUuid: string;
  startDate: string; // use time.formatDateToString ("YYYY-MM-DD" format)
  endDate: string; // use time.formatDateToString ("YYYY-MM-DD" format)
  aggregation: Aggregation;
};

export type SpacePopularityResponse = {
  [uuid: string]: number;
};

export type LiveOccupancyProps = {
  buildingUuid: string;
};

export type LiveOccupancyResponse = {
  [uuid: string]: number;
};

export type SpaceOccupancyProps = {
  buildingUuid: string;
  spaceUuid?: string;
  startDate: string; // use time.formatDateToString ("YYYY-MM-DD" format)
  endDate: string; // use time.formatDateToString ("YYYY-MM-DD" format)
};

export type OccupancyResponse = {
  [timestamp: string]: number;
};

export type ApplessOccupancyEventsProps = {
  buildingUuid: string;
  excludePing?: boolean;
  startDate?: string;
  endDate?: string;
};

export type ApplessOccupancyEventResponse = {
  uuid: string;
  sensor_timestamp: string;
  sensor_local_timestamp: string;
  sensor_id: string;
  event: string;
  event_value: number;
  building_uuid: string;
  space_uuid: string;
  created_at: string;
};

export type ApplessOccupancySensorResponse = {
  uuid: string;
  sensorId: string;
  buildingUuid: string;
  building: any;
  spaceUuid?: string | null;
  space?: any | null;
  reversed: boolean;
  location: string;
  type?: string;
  createdAt: string;
  updatedAt: string | null;
  deletedAt: string | null;
};

export type ForecastRequestProps = {
  buildingUuid: string;
  spaceUuid?: string;
  startDate: string;
  endDate: string;
};

export type ForecastResponseProps = {
  date: string;
  value: number;
};

export type ManageCurrentOccupancyPayload = {
  actualCount?: number | null;
  resetTime?: Date | null;
};

export type ManageCurrentOccupancyResponse = {
  message: string;
};

/* The function `findAllSpaces` is an asynchronous function that makes a GET request to the `/apiv1/spaces/` endpoint and returns 
all the Spaces data from the response. */
const findAllSpaces = async () => {
  const response = await api.get<SpaceProps[]>('/apiv1/spaces/');
  return response?.data;
};

/* The function `findSpaceById` retrieves a Space object from an API based on the provided UUID. */
const findSpaceById = async (uuid: string | undefined) => {
  const response = await api.get<SpaceProps>(`/apiv1/space/${uuid}/`);
  return response?.data;
};

/* The function creates a new Space by making a POST request to the '/apiv1/space/' endpoint with the provided Space data. */
const createSpace = async (newSpace: SpaceProps) => {
  ReactGA.event({
    category: 'User',
    action: 'Create Space',
    label: `Building Uuid: ${newSpace.buildingUuid}, Name: ${newSpace.name}`
  });

  const response = await api.post<SpaceProps>('/apiv1/space/', newSpace);
  return response?.data;
};

/* The function `updateSpace` is an asynchronous function that updates a Space using a PUT request to an API endpoint. */
const updateSpace = async (uuid: string | undefined, updatedSpaces: SpaceProps) => {
  ReactGA.event({
    category: 'User',
    action: 'Update Space',
    label: `Uuid: ${uuid}, Building Uuid: ${updatedSpaces.buildingUuid}, Name: ${updatedSpaces.name}`
  });

  const response = await api.put<SpaceProps>(`/apiv1/space/${uuid}/`, updatedSpaces);
  return response?.data;
};

/* The function `deleteSpaceById` is an asynchronous function that deletes a Space by its UUID using an API call. */
const deleteSpaceById = async (uuid: string | undefined) => {
  ReactGA.event({
    category: 'User',
    action: 'Delete Space',
    label: `Uuid: ${uuid}`
  });

  const response = await api.delete<SpaceProps>(`/apiv1/space/${uuid}/`);
  return response?.data;
};

/**
 * Gets the number of unique occupants who visited a space, averaged for the aggregation type over the given time range.
 * @param aggregatedUsageProps
 * @returns x, y value arrays; x represents the aggregation values, y the average unique users for that aggregation
 */
const spaceAggregatedUniqueOccupants = async (
  aggregatedUsageProps: AggregatedUniqueOccupantsProps
) => {
  const response = await api.post<AggregatedUsageResponse>(
    `/apiv1/aggregated_space_unique_occupants/`,
    aggregatedUsageProps
  );
  return response?.data;
};

/**
 * Gets the number of unique occupants who visited a building, averaged for the aggregation type over the given time range.
 * @param aggregatedUsageProps
 * @returns x, y value arrays; x represents the aggregation values, y the average unique users for that aggregation
 */
const buildingAggregatedUniqueOccupants = async (
  aggregatedUsageProps: AggregatedUniqueOccupantsProps
) => {
  const response = await api.post<BuildingAggregatedUsageResponse>(
    `/apiv1/aggregated_building_unique_occupants/`,
    aggregatedUsageProps
  );
  return response?.data;
};

/**
 * Gets the number of occupants in the building at the same time.
 * @param spaceUsageProps
 * @returns dates and occupancy values for that time interval (10 mins apart)
 */
const spaceOccupancy = async (spaceUsageProps: SpaceOccupancyProps) => {
  const response = await api.post<OccupancyResponse>(`/apiv1/spacial_occupancy/`, spaceUsageProps);
  return response?.data;
};

const getForecast = async (request: ForecastRequestProps) => {
  const response = await forcaseApi.post<ForecastResponseProps[]>('/forecast', request);
  return response?.data;
};

/**
 * Gets the live number of unique occupants in all spaces of a given building.
 * @param liveOccupancyProps
 * @returns map of space uuids -> occupants
 */
const liveOccupancy = async (liveOccupancyProps: LiveOccupancyProps) => {
  const response = await api.post<LiveOccupancyResponse[]>(
    `/apiv1/live_occupancy/`,
    liveOccupancyProps
  );
  return response?.data;
};

const applessOccupancyEvents = async (props: ApplessOccupancyEventsProps) => {
  const { buildingUuid, excludePing, startDate, endDate } = props;
  const queryParams: { [key: string]: any } = {};

  if (excludePing !== undefined) {
    queryParams.exclude_ping = excludePing;
  }
  if (startDate) {
    queryParams.start_date = startDate;
  }
  if (endDate) {
    queryParams.end_date = endDate;
  }

  const response = await api.get<ApplessOccupancyEventResponse[]>(
    `/apiv1/appless_occupancy_events/${buildingUuid}/`,
    {
      params: queryParams
    }
  );
  return response?.data;
};

const applessOccupancySensors = async () => {
  const response = await api.get<ApplessOccupancySensorResponse[]>(
    `/apiv1/appless_occupancy_sensors/`
  );
  return response?.data;
};

/* The function `manageSpaceAlerts` is an asynchronous function that updates the space alerts. */
const updateSpaceAlerts = async (uuid: string | undefined, updatedSpaceAlerts: SpaceAlertProps) => {
  const response = await api.put<{ message: string }>(
    `/apiv1/spaces/manage-alerts/${uuid}/`,
    updatedSpaceAlerts
  );
  return response?.data;
};

/* The function is used to manage current building occupancy by making a POST request to the
'/apiv1/appless_occupancy_events/manage_current_occupancy/${buildingUuid}/' endpoint with the provided payload. */
const manageCurrentOccupancy = async (
  buildingUuid: string,
  payload: ManageCurrentOccupancyPayload
) => {
  const response = await api.post<ManageCurrentOccupancyResponse>(
    `/apiv1/appless_occupancy_events/manage/occupancy/${buildingUuid}/`,
    payload
  );
  return response?.data;
};

const SpaceService = {
  findAllSpaces,
  findSpaceById,
  createSpace,
  updateSpace,
  deleteSpaceById,
  spaceAggregatedUniqueOccupants,
  buildingAggregatedUniqueOccupants,
  spaceOccupancy,
  liveOccupancy,
  applessOccupancyEvents,
  applessOccupancySensors,
  updateSpaceAlerts,
  getForecast,
  manageCurrentOccupancy
};

export default SpaceService;
